import React, { createContext, useContext, useCallback, useMemo, useState } from 'react';
import api from '../ui/api';
import useData from './useData';
import { useAuth } from './auth';

const StudentDataCtx = createContext(null);

export const useStudentData = () => {
  const ctx = useContext(StudentDataCtx);
  if (!ctx) throw new Error('useStudentData must be used inside <StudentDataProvider>');
  return ctx;
};

// Which child a parent last looked at — a per-browser convenience only.
const CHILD_KEY = 'mtzedu.student.child';
const readSavedChild = () => {
  try { return Number(localStorage.getItem(CHILD_KEY)) || null; } catch { return null; }
};
const saveChild = (id) => {
  try { localStorage.setItem(CHILD_KEY, String(id)); } catch { /* not remembered, that's all */ }
};

/** A notification belongs in a child's view when it is about that child
 *  or about no pupil in particular. */
const isForChild = (n, childId) => !n.student_id || Number(n.student_id) === childId;

async function loadChild(id) {
  const [profile, grades, attendance, fees] = await Promise.all([
    api.student(id),
    api.reportCard(id),
    api.attendance(id, { include_present: 1 }),
    api.paymentSummary(id),
  ]);
  return { profile, grades: grades || [], attendance: attendance || [], fees };
}

/**
 * Loads the whole record of EVERY pupil this login may see, ONCE, and
 * shares it with every tab.
 *
 * A pupil sees only themself; a father/mother sees every child of the
 * family (GET /students). All children load in parallel, so switching
 * child — like switching tab — costs nothing, and the Home page can show
 * the whole family side by side. Pages read the SELECTED child through
 * the same fields as before (profile, grades, attendance, fees), so none
 * of them needs to know about families.
 *
 * Notifications are held in their own state rather than inside the
 * loaded bundle, because marking one read has to update the badge
 * immediately without refetching the marks and the fees alongside it.
 */
export function StudentDataProvider({ children }) {
  const { studentId } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [chosenId, setChosenId] = useState(readSavedChild);

  const { data, loading, error, reload } = useData(async () => {
    const list = await api.myStudents();
    const kids = list && list.length ? list : [{ id: studentId, is_linked: 1 }];
    const [bundles, inbox] = await Promise.all([
      Promise.all(kids.map((k) => loadChild(k.id))),
      api.notifications({ limit: 100 }),
    ]);
    setNotifications(inbox || []);
    return kids.map((k, i) => ({ ...k, ...bundles[i] }));
  }, [studentId]);

  const family = useMemo(() => data || [], [data]);
  const current = family.find((c) => c.id === chosenId)
    || family.find((c) => c.is_linked)
    || family[0]
    || null;
  const currentId = current?.id ?? null;

  const selectChild = useCallback((id) => {
    setChosenId(id);
    saveChild(id);
  }, []);

  const markRead = useCallback(async (id) => {
    // Optimistic: the badge should drop the moment it's tapped. A failed
    // call just leaves the row read locally until the next full reload,
    // which is a better trade than a badge that lags behind the tap.
    setNotifications((list) => list.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
    try { await api.markRead(id); } catch { /* corrected on next reload */ }
  }, []);

  const hasSiblings = family.length > 1;
  const markAllRead = useCallback(async () => {
    // With several children, "all" means all in the child on screen.
    setNotifications((list) => list.map((n) => (
      !hasSiblings || isForChild(n, currentId) ? { ...n, is_read: 1 } : n
    )));
    try { await api.markAllRead(hasSiblings ? { student_id: currentId } : undefined); } catch { /* corrected on next reload */ }
  }, [hasSiblings, currentId]);

  const value = useMemo(() => {
    const mine = hasSiblings ? notifications.filter((n) => isForChild(n, currentId)) : notifications;
    return {
      family: family.map((c) => ({
        ...c,
        // The switcher's dot: unread messages about this child only.
        unread: notifications.filter((n) => !n.is_read && Number(n.student_id) === c.id).length,
      })),
      currentId,
      hasSiblings,
      selectChild,
      profile: current?.profile || null,
      grades: current?.grades || [],
      attendance: current?.attendance || [],
      fees: current?.fees || null,
      notifications: mine,
      unreadCount: mine.filter((n) => !n.is_read).length,
      loading,
      error,
      reload,
      markRead,
      markAllRead,
    };
  }, [family, current, currentId, hasSiblings, selectChild, notifications, loading, error, reload, markRead, markAllRead]);

  return <StudentDataCtx.Provider value={value}>{children}</StudentDataCtx.Provider>;
}
